import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type FormType = "all" | "contact" | "partner" | "consign" | "review";

const submissionsQuery = queryOptions({
  queryKey: ["admin-submissions"],
  queryFn: async () => {
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth.user?.id;
    if (!uid) return { isAdmin: false, rows: [] };
    const { data: isAdmin } = await supabase.rpc("has_role", { _user_id: uid, _role: "admin" });
    if (!isAdmin) return { isAdmin: false, rows: [] };
    const { data, error } = await supabase
      .from("contact_submissions")
      .select("id, name, email, phone, message, subject, form_type, created_at")
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) throw error;
    return { isAdmin: true, rows: data ?? [] };
  },
});

export const Route = createFileRoute("/_authenticated/admin/submissions")({
  head: () => ({
    meta: [
      { title: "Form Inbox | Avolus" },
      { name: "description", content: "Private inbox of partner, consign and contact form submissions." },
      { property: "og:title", content: "Form Inbox | Avolus" },
      { property: "og:description", content: "Private Avolus form inbox." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(submissionsQuery),
  errorComponent: ({ error }) => <p className="p-10 text-destructive">Couldn't load the inbox: {error instanceof Error ? error.message : "Unknown error"}</p>,
  notFoundComponent: () => <p className="p-10">Not found.</p>,
  component: Inbox,
});

const LABELS: Record<string, string> = { contact: "Contact", partner: "Partner", consign: "Consign", review: "Review" };

function Inbox() {
  const { data } = useSuspenseQuery(submissionsQuery);
  const [filter, setFilter] = useState<FormType>("all");
  const [open, setOpen] = useState<string | null>(null);

  if (!data.isAdmin) {
    return (
      <main className="mx-auto max-w-xl p-10 text-center">
        <h1 className="text-2xl font-semibold">No access</h1>
        <p className="mt-3 text-muted-foreground">This account can't view the form inbox. Sign in as info@avoluscars.com.</p>
        <button className="mt-6 underline" onClick={() => supabase.auth.signOut().then(() => location.assign("/auth?next=/admin/submissions"))}>
          Switch account
        </button>
      </main>
    );
  }

  const rows = data.rows.filter((r) => filter === "all" || r.form_type === filter);
  const count = (t: string) => data.rows.filter((r) => r.form_type === t).length;

  return (
    <main className="mx-auto max-w-5xl px-5 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold">Form Inbox</h1>
          <p className="text-sm text-muted-foreground">Newest first. Click a row to read the full form.</p>
        </div>
        <button className="text-sm underline" onClick={() => supabase.auth.signOut().then(() => location.assign("/"))}>Sign out</button>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {(["all", "partner", "consign", "review", "contact"] as FormType[]).map((t) => (
          <button
            key={t}
            onClick={() => setFilter(t)}
            className={`rounded-full border border-border px-4 py-1.5 text-sm ${filter === t ? "bg-primary text-primary-foreground" : "bg-card text-foreground"}`}
          >
            {t === "all" ? `All (${data.rows.length})` : `${LABELS[t]} (${count(t)})`}
          </button>
        ))}
      </div>

      <ul className="mt-6 divide-y divide-border rounded-lg border border-border bg-card">
        {rows.length === 0 && <li className="p-6 text-center text-muted-foreground">No submissions yet.</li>}
        {rows.map((r) => (
          <li key={r.id}>
            <button className="w-full p-4 text-left" onClick={() => setOpen(open === r.id ? null : r.id)}>
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded bg-muted px-2 py-0.5 text-xs uppercase tracking-wide text-muted-foreground">{LABELS[r.form_type] ?? r.form_type}</span>
                <span className="font-medium">{r.name}</span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {new Date(r.created_at).toLocaleString("en-CA", { timeZone: "America/Vancouver", dateStyle: "medium", timeStyle: "short" })}
                </span>
              </div>
              {r.subject && <p className="mt-1 text-sm text-muted-foreground">{r.subject}</p>}
            </button>
            {open === r.id && (
              <div className="space-y-2 px-4 pb-5 text-sm">
                <p><a className="underline" href={`mailto:${r.email}`}>{r.email}</a>{r.phone && <> · <a className="underline" href={`tel:${r.phone}`}>{r.phone}</a></>}</p>
                <pre className="whitespace-pre-wrap rounded bg-muted p-3 font-sans text-foreground">{r.message}</pre>
              </div>
            )}
          </li>
        ))}
      </ul>
    </main>
  );
}
